// src/contexts/EntitlementContext.tsx
// Eixo de ENTITLEMENT por módulo de tenant — paralelo ao AuthContext (ADR-002:
// cada eixo de autorização centralizado em um lugar). Diferente de hasPermission,
// `hasModule` NÃO tem bypass de ROLE_MASTER (premissa #8 escopa o bypass a
// hasPermission). É só UX: o backend é o gate real (premissa #1).
//
// FAIL-OPEN: enquanto carrega, em erro, ou catálogo completo (gate desligado) →
// hasModule retorna `true` (nada some). Só retorna `false` quando a resposta
// chega (200) e `modules` NÃO contém o módulo.
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { apiGet } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import type { EntitlementFeatures } from '@/types/entitlement';
import { moduleForArea } from '@/config/moduleAreas';
import { logger } from '@/utils/logger';

type EntitlementStatus = 'idle' | 'loading' | 'loaded' | 'error';

interface EntitlementContextType {
  modules: string[];
  effectiveStatus: string | null;
  limits: Record<string, string>;
  status: EntitlementStatus;
  /** `true` (fail-open) até a resposta chegar; depois reflete `modules`. Sem bypass de role. */
  hasModule: (mod: string) => boolean;
  /** `true` se a área não é gateada por nenhum módulo, senão delega a hasModule. */
  isAreaEnabled: (area: string) => boolean;
  refresh: () => void;
}

const EntitlementContext = createContext<EntitlementContextType | undefined>(undefined);

export const EntitlementProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isInitialized } = useAuth();
  const [modules, setModules] = useState<string[]>([]);
  const [effectiveStatus, setEffectiveStatus] = useState<string | null>(null);
  const [limits, setLimits] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<EntitlementStatus>('idle');

  const fetchFeatures = useCallback(async () => {
    setStatus('loading');
    try {
      const res = await apiGet('/v1/entitlement/features');
      const data = res.data as EntitlementFeatures;
      setModules(Array.isArray(data?.modules) ? data.modules : []);
      setEffectiveStatus(data?.effectiveStatus ?? null);
      setLimits(data?.limits ?? {});
      setStatus('loaded');
    } catch (err) {
      // Fail-open: o gate de UX não pode esconder telas por falha de rede.
      // O backend continua sendo o gate real (premissa #1).
      // Loga só a mensagem — não o erro Axios cru, que carrega config.headers
      // com o Bearer token (§E). O interceptor de api.ts já loga url/status.
      logger.error(
        'Falha ao carregar /v1/entitlement/features (fail-open):',
        err instanceof Error ? err.message : String(err)
      );
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    if (!isInitialized) return;
    if (isAuthenticated) {
      fetchFeatures();
    } else {
      // Reset ao deslogar (idle = fail-open também, mas sem dados de tenant antigo).
      setModules([]);
      setEffectiveStatus(null);
      setLimits({});
      setStatus('idle');
    }
  }, [isInitialized, isAuthenticated, fetchFeatures]);

  const hasModule = useCallback(
    (mod: string): boolean => {
      if (status !== 'loaded') return true; // idle | loading | error → fail-open
      return modules.includes(mod);
    },
    [status, modules]
  );

  const isAreaEnabled = useCallback(
    (area: string): boolean => {
      const mod = moduleForArea(area);
      if (!mod) return true; // área não gateada por nenhum módulo
      return hasModule(mod);
    },
    [hasModule]
  );

  return (
    <EntitlementContext.Provider
      value={{ modules, effectiveStatus, limits, status, hasModule, isAreaEnabled, refresh: fetchFeatures }}
    >
      {children}
    </EntitlementContext.Provider>
  );
};

export const useEntitlement = (): EntitlementContextType => {
  const context = useContext(EntitlementContext);
  if (!context) {
    throw new Error('useEntitlement deve ser usado dentro de um EntitlementProvider');
  }
  return context;
};
